// src/components/looker/admin/TemplateActionsMenu.tsx
/**
 * The ⋯ button on a template row and the actions behind it. Same shape as the
 * account row's menu in Settings (PlatformAccountRow): plain buttons in a
 * popup, closed by an outside press, Escape, or choosing an action.
 *
 * The popup is position: fixed, placed from the trigger's rectangle. The
 * table sits in an overflow-x-auto section, and an absolute popup in the last
 * row would be clipped by it; a fixed one is not. Scrolling or resizing closes
 * it rather than leaving it floating away from its row.
 *
 * "Copy link" keeps the popup open, so its "Link copied" can be read.
 *
 * @module components/looker/admin/TemplateActionsMenu
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDismissable } from '@/hooks/useDismissable';
import type { LookerTemplateSummary } from '@/services/looker/lookerTypes';

interface Props {
  template: LookerTemplateSummary;
  busy: boolean;
  /** The Copy link item's text: "Copy link", or the result of the last copy. */
  copyLinkLabel: string;
  onEdit: () => void;
  /** Opens the tile builder; offered only for a template made in the app. */
  onEditTiles: () => void;
  onCopy: () => void;
  onMakeDefault: () => void;
  onCopyLink: () => void;
  onDelete: () => void;
}

/** Room the popup needs below the trigger before it opens upwards instead, px. */
const POPUP_HEIGHT = 280;
const GAP = 4;

const ITEM = 'block w-full px-3 py-1.5 text-left text-sm text-ink-900 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent';
const DANGER_ITEM = 'block w-full px-3 py-1.5 text-left text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent';

type Placement = { right: number; top?: number; bottom?: number };

export const TemplateActionsMenu: React.FC<Props> = ({
  template, busy, copyLinkLabel, onEdit, onEditTiles, onCopy, onMakeDefault, onCopyLink, onDelete,
}) => {
  const { t } = useTranslation();
  const [placement, setPlacement] = useState<Placement | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const open = placement !== null;

  const close = useCallback(() => {
    setPlacement(null);
    triggerRef.current?.focus();
  }, []);
  useDismissable(wrapRef, open, close);

  useEffect(() => {
    if (!open) return;
    const dismiss = () => setPlacement(null);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open]);

  const toggle = () => {
    if (open) return close();
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const right = window.innerWidth - r.right;
    const fitsBelow = r.bottom + GAP + POPUP_HEIGHT <= window.innerHeight;
    setPlacement(fitsBelow ? { right, top: r.bottom + GAP } : { right, bottom: window.innerHeight - r.top + GAP });
  };

  const run = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <div className="relative inline-block" ref={wrapRef}>
      {/* No aria-haspopup, as in PlatformAccountRow: plain buttons, not a role="menu". */}
      <button
        type="button"
        ref={triggerRef}
        aria-label={t('looker.admin.actionsFor', { title: template.title })}
        aria-expanded={open}
        onClick={toggle}
        className="rounded-md p-1.5 text-neutral-700 hover:bg-neutral-100"
      >
        <MoreHorizontal size={16} aria-hidden="true" />
      </button>

      {placement && (
        <div
          className="fixed z-50 w-48 rounded-md border border-neutral-200 bg-white py-1 shadow-lg"
          style={{ right: placement.right, top: placement.top, bottom: placement.bottom }}
        >
          <button type="button" className={ITEM} disabled={busy} onClick={run(onEdit)}>{t('looker.admin.edit')}</button>
          {template.appBuilt && (
            <button type="button" className={ITEM} disabled={busy} onClick={run(onEditTiles)}>{t('looker.admin.editTiles')}</button>
          )}
          <button type="button" className={ITEM} disabled={busy} onClick={run(onCopy)}>{t('looker.admin.copy')}</button>
          {!template.isDefault && (
            <button type="button" className={ITEM} disabled={busy} onClick={run(onMakeDefault)}>{t('looker.admin.makeDefault')}</button>
          )}
          {template.lookerUrl && (
            <>
              <a className={ITEM} href={template.lookerUrl} target="_blank" rel="noopener noreferrer" onClick={() => setPlacement(null)}>
                {t('looker.admin.openInLooker')}
              </a>
              <button type="button" className={ITEM} onClick={onCopyLink}>{copyLinkLabel}</button>
            </>
          )}
          <div className="my-1 border-t border-neutral-100" />
          <button
            type="button"
            className={DANGER_ITEM}
            disabled={busy || template.isDefault === true}
            title={template.isDefault ? t('looker.admin.deleteDefaultHint') : undefined}
            onClick={run(onDelete)}
          >
            {t('looker.admin.delete')}
          </button>
        </div>
      )}
    </div>
  );
};
